import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloudUploadOutlined,
  DeleteOutline,
  OpenInNew,
  Refresh,
  InsertDriveFileOutlined,
  FolderOpenOutlined,
} from "@mui/icons-material";
import { useAIRequests } from "../context/AIRequestContext";
import projectContextService from "../services/projectContextService";
import { SectionCard, EmptyState, ListSkeleton, ConfirmDialog, IconTile } from "./pagekit";

const SUPPORTED_HINT =
  ".txt, .md, .csv, .xlsx, .xls, .docx, .pdf, .png, .jpg, .webp up to 10 MB; .pptx up to 15 MB";

/**
 * Format byte counts for upload metadata.
 * @param {number} bytes - Raw byte size.
 * @returns {string} Human-readable size label.
 */
function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** idx)).toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

/**
 * Render status color for an ingested context file.
 * @param {string} status - File processing status.
 * @returns {"default"|"primary"|"success"|"error"|"warning"} MUI chip color.
 */
function statusColor(status) {
  if (status === "completed") return "success";
  if (status === "processing") return "primary";
  if (status === "failed") return "error";
  if (status === "pending") return "warning";
  return "default";
}

/**
 * Project-level files upload and ingestion panel.
 * @param {object} props - Component props.
 * @param {object} props.project - Active project row.
 * @param {string} props.accountId - Active account id.
 * @returns {JSX.Element|null} Project context panel.
 */
function ProjectContextPanel({ project, accountId }) {
  const inputRef = useRef(null);
  const { createRequest, refreshRequests } = useAIRequests();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const projectId = project?.id;

  const loadFiles = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const rows = await projectContextService.listProjectContextFiles(projectId);
      setFiles(rows);
    } catch (err) {
      setError(err?.message || "Failed to load project context files.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const canUpload = useMemo(() => Boolean(projectId && accountId && !uploading), [
    accountId,
    projectId,
    uploading,
  ]);

  const handleFiles = useCallback(
    async (selected) => {
      const selectedFiles = Array.from(selected || []);
      if (!selectedFiles.length || !projectId || !accountId) return;

      setUploading(true);
      setError("");
      setInfo("");
      try {
        for (const file of selectedFiles) {
          const request = await createRequest({
            type: "project_context_ingestion",
            title: `Indexing ${file.name}`,
            projectId,
            data: {
              accountId,
              fileName: file.name,
            },
          });
          await projectContextService.uploadProjectContextFile({
            file,
            projectId,
            accountId,
            requestId: request.id,
          });
        }
        setInfo("Upload queued. The jobs panel will show indexing progress.");
        await refreshRequests();
        await loadFiles();
      } catch (err) {
        setError(err?.message || "Failed to upload project context.");
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [accountId, createRequest, loadFiles, projectId, refreshRequests]
  );

  const handleDeleteFile = useCallback(
    async (file) => {
      setFileToDelete(null);
      setDeletingId(file.id);
      setError("");
      setInfo("");
      try {
        await projectContextService.deleteProjectContextFile(file);
        setInfo(`Deleted ${file.name}.`);
        await loadFiles();
      } catch (err) {
        setError(err?.message || "Failed to delete project file.");
      } finally {
        setDeletingId(null);
      }
    },
    [loadFiles]
  );

  const handleOpenFile = useCallback(async (file) => {
    setOpeningId(file.id);
    setError("");
    try {
      const url = await projectContextService.createProjectContextFileViewUrl(file);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err?.message || "Failed to open project file.");
    } finally {
      setOpeningId(null);
    }
  }, []);

  if (!project) return null;

  return (
    <SectionCard
      flush
      title="Context files"
      description="Reference material Inky can draw on when writing documents: specs, spreadsheets, decks, images."
      actions={
        <>
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={loadFiles} disabled={loading} aria-label="Refresh context files">
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadOutlined />}
            disabled={!canUpload}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Uploading..." : "Upload files"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => handleFiles(event.target.files)}
          />
        </>
      }
    >
      {uploading && <LinearProgress aria-label="Uploading" />}
      {(error || info) && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          {info && <Alert severity="success" onClose={() => setInfo("")}>{info}</Alert>}
        </Box>
      )}

      {loading && !files.length ? (
        <ListSkeleton rows={2} label="Loading context files" />
      ) : files.length === 0 ? (
        <EmptyState
          compact
          icon={<FolderOpenOutlined />}
          title="No context files"
          description={`Add files the AI should know about. Supported: ${SUPPORTED_HINT}.`}
          actions={
            <Button
              variant="outlined"
              startIcon={<CloudUploadOutlined />}
              disabled={!canUpload}
              onClick={() => inputRef.current?.click()}
            >
              Upload files
            </Button>
          }
        />
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0 }}>
          {files.map((file) => (
            <Box
              component="li"
              key={file.id}
              sx={{
                listStyle: "none",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                borderBottom: 1,
                borderColor: "var(--q-row-divider)",
                "&:last-of-type": { borderBottom: 0 },
              }}
            >
              <IconTile tone="neutral" size={32}>
                <InsertDriveFileOutlined />
              </IconTile>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={file.name}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {[file.source_type, formatBytes(Number(file.size_bytes)), file.summary || "Summary appears after indexing."]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
                {file.error_message && (
                  <Typography variant="caption" color="error" display="block">
                    {file.error_message}
                  </Typography>
                )}
              </Box>
              <Chip size="small" label={file.status} color={statusColor(file.status)} />
              <Tooltip title="Open file">
                <span>
                  <IconButton
                    size="small"
                    disabled={openingId === file.id}
                    onClick={() => handleOpenFile(file)}
                    aria-label={`Open ${file.name}`}
                  >
                    {openingId === file.id ? <CircularProgress size={16} /> : <OpenInNew fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete file">
                <span>
                  <IconButton
                    size="small"
                    disabled={deletingId === file.id}
                    onClick={() => setFileToDelete(file)}
                    aria-label={`Delete ${file.name}`}
                  >
                    {deletingId === file.id ? <CircularProgress size={16} /> : <DeleteOutline fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(fileToDelete)}
        title="Delete context file?"
        message={`"${fileToDelete?.name || ""}" and its indexed context will be removed from this project.`}
        confirmLabel="Delete file"
        onConfirm={() => fileToDelete && handleDeleteFile(fileToDelete)}
        onClose={() => setFileToDelete(null)}
      />
    </SectionCard>
  );
}

export default ProjectContextPanel;
